import moment from "moment";
import { CSVLink } from "react-csv";
import { Button, Dropdown } from "semantic-ui-react";
import type { FoodOrder } from "types";
import writeXlsxFile from "write-excel-file";

/**
 * What the invoice export control accepts. Only call site is `./index.tsx`.
 *
 * `ordersData` is `FoodOrder[] | undefined` because the caller passes
 * `ordersData && ordersData`, and `ordersData` is SWR 0.5.7's optional `data`.
 * `selectedOrdersLength` is never read — the component derives what it needs
 * from `selectedOrderIds`.
 */
type ExportInvoiceProps = {
    ordersData?: FoodOrder[] | undefined;
    /** Ids ticked in the table; the export is filtered down to these. */
    selectedOrderIds: string[];
    /** Accepted but unread. */
    selectedOrdersLength?: number;
    /** Vendor code, used only to name the downloaded file and to hide CSV for 'AB'. */
    vendor?: string | undefined;
};

/**
 * One column of the workbook/CSV schema, in the shape `write-excel-file` expects.
 *
 * The `value` reader is typed against {@link FoodOrder} rather than the library's
 * own generic so that a mis-spelled field name here is an error.
 */
type ExportColumn = {
    column: string;
    value: (order: FoodOrder) => string | number | undefined;
};

function ExportInvoice(props: ExportInvoiceProps){
    const exportData = props.ordersData && props.ordersData.filter(o => props.selectedOrderIds.includes(o.orderId!))

    // bundleName is orderName for orders created from table
    /*
     * SUSPECTED DEFECT (the 'Total' column below) — REPORTED, NOT FIXED.
     * `parseFloat(order.foodPricing + order.shippingFees)` adds BEFORE parsing.
     * {@link FoodOrder} declares both as `number | string`, and on the string arm
     * `+` concatenates: `'42.50' + '7.25'` is `'42.507.25'`, which `parseFloat`
     * reads as `42.507` — a silently wrong invoice total, not a crash. The other
     * two money columns parse each operand separately and are correct.
     *
     * It does not misfire today only because `./index.tsx:115-116` rewrites both
     * fields to numbers before this component sees them. That normalisation is
     * two files away and nothing enforces it; `export.spec.tsx:279` pins the
     * numeric path only. The fix is `parseFloat(order.foodPricing) +
     * parseFloat(order.shippingFees)`, which is a behaviour change on the string
     * arm and so is left alone.
     */
    const schema: ExportColumn[] = [{column:'orderId', value: order=>order.orderId},{column:'orderDate', value: order=>order.orderDate},{column:'deliveryDate', value: order=>order.deliveryDate},{column:'patientId', value: order=>order.patientId},{column:'bundleId', value: order=>order.bundleId},{column:'orderName', value: order=>order.orderName},{column:'bundleQuantity', value: order=>order.bundleQuantity},{column: 'items', value: order=> order.orderItems? order.orderItems.map(oi => oi.name).join('\n'):''},{column: 'Food Price', value: order=> order.foodPricing? parseFloat(String(order.foodPricing)).toFixed(2): ''},{column: 'Shipping Fees', value: order=> Object.prototype.hasOwnProperty.call(order, 'shippingFees')? parseFloat(String(order.shippingFees)).toFixed(2): ''}, {column: 'Total', value: order=> Object.prototype.hasOwnProperty.call(order, 'foodPricing') && Object.prototype.hasOwnProperty.call(order, 'shippingFees')? parseFloat((order.foodPricing! as number)+(order.shippingFees! as number) as unknown as string).toFixed(2):''}]

    const handleExport =() => {
        // props.markExported()
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- fire-and-forget browser call; a rejection silently produces no file
        (writeXlsxFile as (data: FoodOrder[] | undefined, options: { schema: unknown; fileName: string }) => Promise<void>)(exportData, {
            schema,
            fileName: props.vendor+'-'+moment().utc().format().toString()+'.xlsx'
          })
    }
    const csvButton = <CSVLink
        style={{color:"black"}}
        // onClick = {props.markExported}
        filename={props.vendor+'-'+moment().utc().format().toString()}
        data={exportData! && exportData}
        headers={schema.map(sc => {return sc.column})}>
        AS CSV
    </CSVLink>
    return (
        <Dropdown as={Button} color='green' icon='download'>
            <Dropdown.Menu>
                {props.vendor!=='AB' && <Dropdown.Item key="1">{csvButton}</Dropdown.Item>}
                <Dropdown.Item key="2" onClick={handleExport}><a>AS EXCEL</a></Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    )
}

export default ExportInvoice;